import type { ComponentProps, JSX } from 'react';

import { EloProgressCircle } from '@plurall/elo-v3';

import {
  GlobalLoaderBackdropFactory,
  GlobalLoaderContentFactory,
  GlobalLoaderLabelFactory,
} from './global-loader.factory';
import { useGlobalLoadingIndicator } from './hooks/useGlobalLoadingIndicator';

const DEFAULT_LABEL = 'Carregando…';

type LoaderMode = 'progress' | 'backdrop';

type GlobalRequestLoaderProps = {
  mode?: LoaderMode;
  label?: string;
  ariaLabel?: string;
  withBackdrop?: boolean;
};

function ProgressCircle(props: ComponentProps<typeof EloProgressCircle>): JSX.Element {
  return (
    <EloProgressCircle
      data-test-id='elo-progress'
      variant='primary'
      {...props}
    />
  );
}

export function GlobalRequestLoader({
  mode = 'progress',
  label = DEFAULT_LABEL,
  ariaLabel,
  withBackdrop = true,
}: GlobalRequestLoaderProps): JSX.Element | null {
  const { progress, shouldShowProgress, shouldShowBackdrop } = useGlobalLoadingIndicator({ mode });

  if (shouldShowProgress) {
    return (
      <ProgressCircle
        size='small'
        type='value'
        value={progress}
        valueText={`${Math.round(progress)}%`}
      />
    );
  }

  if (!shouldShowBackdrop) return null;

  const ariaLabelResolved = ariaLabel ?? label;
  const content = (
    <GlobalLoaderContentFactory>
      <ProgressCircle
        icon='book'
        size='large'
        type='standalone'
        valueText={label}
        aria-label={ariaLabelResolved}
      />
      {label ? <GlobalLoaderLabelFactory>{label}</GlobalLoaderLabelFactory> : null}
    </GlobalLoaderContentFactory>
  );

  if (withBackdrop) {
    return (
      <GlobalLoaderBackdropFactory
        role='status'
        aria-label={ariaLabelResolved}
        data-testid='global-loader-backdrop'>
        {content}
      </GlobalLoaderBackdropFactory>
    );
  }

  return content;
}
